import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Clock, PiggyBank, Target, Wallet } from 'lucide-react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import Layout from '../components/layout/AppLayout';
import Header from '../../shared/components/layout/Header';
import Loader from '../../shared/components/ui/Loader';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import { useSmartBack } from '../../shared/navigation';
import {
  EmptyRow,
  SectionCard,
  SegmentedControl,
  StatusPill,
  Table,
  TableCard,
  TBody,
  Td,
  Th,
  THead,
  Tile,
  TileRow
} from '../../shared/components/ui/primitives';
import { getProjectExpenditureDetail } from '../services/api/implementation/impl';
import { compactMoney, money, shortDate } from './SiteWallet/siteWalletUi';

// Categorical slots 1–4, in fixed order, validated together (CVD and normal
// vision pass). Aqua and yellow sit under 3:1 on white, so the chart always
// has a legend and the "By kind" table beside it.
const KINDS = [
  { key: 'material', label: 'Material', color: '#2a78d6', hint: 'Vendor PIs' },
  {
    key: 'service_providers',
    label: 'Service providers',
    color: '#eb6834',
    hint: 'Task bills, and running bills net of retention and TDS'
  },
  {
    key: 'labour',
    label: 'Labour wages (Operations)',
    color: '#1baf7a',
    hint: 'Wages and advances paid directly'
  },
  { key: 'site', label: 'Site expenses (wallet)', color: '#eda100', hint: 'Approved site wallet spends' }
];

const ITEM_TABS = [
  { value: 'material', label: 'Vendor PIs' },
  { value: 'service_providers', label: 'Service provider bills' },
  { value: 'labour', label: 'Labour payments' },
  { value: 'site', label: 'Site expenses (wallet)' }
];

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const monthLabel = (ym) => {
  const [y, m] = String(ym).split('-');
  return `${MONTHS[Number(m) - 1]} ${String(y).slice(2)}`;
};

const pct = (value) => (value == null ? '—' : `${value}%`);

const STATUS_TONE = {
  paid: 'success',
  partial: 'info',
  pending: 'warning',
  Paid: 'success',
  Unpaid: 'warning',
  SETTLED: 'success',
  CERTIFIED: 'info',
  'Reimbursement due': 'warning'
};
const STATUS_LABEL = {
  paid: 'Paid',
  partial: 'Partially paid',
  pending: 'Pending',
  SETTLED: 'Settled',
  CERTIFIED: 'Certified'
};

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div className="rounded-md border border-gray-200 bg-white px-3 py-2 text-sm shadow-sm">
      <p className="mb-1 font-semibold text-gray-800">{monthLabel(label)}</p>
      {KINDS.map((k) => (
        <p key={k.key} className="flex items-center justify-between gap-6 text-gray-600">
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: k.color }} />
            {k.label}
          </span>
          <span className="tabular-nums text-gray-900">{money(row[k.key])}</span>
        </p>
      ))}
      <p className="mt-1 flex justify-between gap-6 border-t border-gray-100 pt-1 font-semibold text-gray-900">
        <span>Total</span>
        <span className="tabular-nums">{money(row.total)}</span>
      </p>
    </div>
  );
};

const MiniTable = ({ columns, rows, empty }) =>
  rows.length ? (
    <table className="w-full text-sm">
      <thead className="text-left text-gray-500">
        <tr>
          {columns.map((c) => (
            <th key={c.label} className={`py-1 font-medium ${c.right ? 'text-right' : ''}`}>
              {c.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {rows.map((row, i) => (
          <tr key={i}>
            {columns.map((c) => (
              <td key={c.label} className={`py-1.5 ${c.right ? 'text-right tabular-nums' : ''}`}>
                {c.render(row)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  ) : (
    <p className="text-sm text-gray-500">{empty}</p>
  );

/**
 * One project's spend in full: against its budget, month by month, by kind
 * and block, its top vendors and materials, and the PIs, bills and payments
 * behind every total.
 */
const ProjectExpenditureDetail = () => {
  const { id } = useParams();
  const { collapsed } = useSidebar();
  const { onBack } = useSmartBack({ fallback: '/builder/reports/accounts' });
  const [tab, setTab] = useState('material');

  const detail = useQuery({
    queryKey: ['projectExpenditureDetail', id],
    queryFn: () => getProjectExpenditureDetail(id),
    keepPreviousData: true,
    enabled: !!id
  });
  const d = detail.data;
  const p = d?.project;
  const items = d?.items?.[tab] ?? [];
  const chartHasData = (d?.monthly ?? []).some((m) => m.total > 0);

  const itemColumns = {
    material: [
      { label: 'PI', render: (r) => r.pi_number },
      { label: 'Vendor', render: (r) => r.vendor ?? '—' },
      { label: 'Block', render: (r) => r.block ?? 'Project stock' },
      { label: 'PO', render: (r) => r.po_number ?? '—' },
      { label: 'Date', render: (r) => shortDate(r.date) }
    ],
    service_providers: [
      { label: 'Bill', render: (r) => r.number },
      { label: 'Type', render: (r) => r.type },
      { label: 'Service provider', render: (r) => r.party ?? '—' },
      { label: 'Date', render: (r) => shortDate(r.date) }
    ],
    labour: [
      { label: 'Paid on', render: (r) => shortDate(r.date) },
      { label: 'Paid to', render: (r) => r.party ?? '—' },
      { label: 'Mode', render: (r) => r.mode ?? '—' },
      { label: 'Reference', render: (r) => r.reference || '—' }
    ],
    site: [
      { label: 'Spent on', render: (r) => shortDate(r.date) },
      { label: 'Spent by', render: (r) => r.party ?? '—' },
      { label: 'Category', render: (r) => r.category ?? '—' },
      { label: 'Note', render: (r) => r.note || '—' }
    ]
  }[tab];
  const withAmounts = tab !== 'labour';

  return (
    <Layout>
      <Loader isVisible={detail.isFetching && !d} />
      <div
        className={`fixed top-0 z-10 bg-white transition-all duration-300 ease-in-out ${
          collapsed ? 'left-[76px] w-[calc(100%-76px)]' : 'left-64 w-[calc(100%-16rem)]'
        }`}
      >
        <Header
          data={{ name: p ? `${p.name} · Expenditure` : 'Project expenditure' }}
          backArrow
          onBack={onBack}
        />
      </div>

      <div className="mt-[5.5rem] flex flex-col gap-4 bg-gray-100 px-3 pb-8 pt-2">
        {detail.isError ? (
          <p className="text-sm text-danger-fg">
            Couldn&apos;t load this project:{' '}
            {detail.error?.response?.data?.message || detail.error?.message}
          </p>
        ) : (
          p && (
            <>
              <TileRow>
                <Tile
                  variant="card"
                  label="Budget"
                  hint={p.budget ? 'Estimated budget' : 'Not set on the project'}
                  value={p.budget ? money(p.budget) : '—'}
                  icon={<Target size={16} />}
                />
                <Tile
                  variant="card"
                  label="Spent"
                  hint="Paid so far"
                  value={money(p.total.paid)}
                  icon={<Wallet size={16} />}
                  tone="success"
                />
                <Tile
                  variant="card"
                  label="Pending to pay"
                  hint="Billed, not yet paid"
                  value={money(p.total.pending)}
                  icon={<Clock size={16} />}
                  tone="warning"
                />
                <Tile
                  variant="card"
                  label="Budget used"
                  hint={
                    p.budget
                      ? p.budget_remaining < 0
                        ? `${money(-p.budget_remaining)} over`
                        : `${money(p.budget_remaining)} left`
                      : 'No budget to compare'
                  }
                  value={pct(p.budget_used_pct)}
                  icon={<PiggyBank size={16} />}
                  tone={
                    p.budget_used_pct > 100
                      ? 'danger'
                      : p.budget_used_pct >= 80
                        ? 'warning'
                        : 'info'
                  }
                />
              </TileRow>

              <SectionCard title="Paid by month" subtitle="Last 12 months">
                {chartHasData ? (
                  <>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={d.monthly}
                          margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
                        >
                          <CartesianGrid vertical={false} stroke="#EEF2F6" />
                          <XAxis
                            dataKey="month"
                            tickFormatter={monthLabel}
                            tick={{ fontSize: 11, fill: '#6B7280' }}
                            axisLine={false}
                            tickLine={false}
                          />
                          <YAxis
                            tick={{ fontSize: 11, fill: '#6B7280' }}
                            axisLine={false}
                            tickLine={false}
                            width={56}
                            tickFormatter={compactMoney}
                          />
                          <Tooltip content={<ChartTooltip />} cursor={{ fill: '#F1F5F9' }} />
                          {KINDS.map((k, i) => (
                            <Bar
                              key={k.key}
                              dataKey={k.key}
                              name={k.label}
                              stackId="spend"
                              fill={k.color}
                              stroke="#ffffff"
                              strokeWidth={2}
                              maxBarSize={36}
                              radius={i === KINDS.length - 1 ? [4, 4, 0, 0] : 0}
                            />
                          ))}
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="mt-2 flex flex-wrap justify-center gap-x-4 gap-y-1">
                      {KINDS.map((k) => (
                        <span
                          key={k.key}
                          className="inline-flex items-center gap-1.5 text-xs text-gray-600"
                        >
                          <span
                            className="h-2 w-2 rounded-full"
                            style={{ backgroundColor: k.color }}
                          />
                          {k.label}
                        </span>
                      ))}
                    </div>
                  </>
                ) : (
                  <p className="py-10 text-center text-sm text-gray-500">
                    No payments in this period.
                  </p>
                )}
              </SectionCard>

              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <SectionCard title="By kind">
                  <MiniTable
                    rows={KINDS.map((k) => ({ ...k, ...p[k.key] }))}
                    empty=""
                    columns={[
                      {
                        label: 'Kind',
                        render: (r) => (
                          <span className="inline-flex items-center gap-1.5" title={r.hint}>
                            <span
                              className="h-2 w-2 rounded-full"
                              style={{ backgroundColor: r.color }}
                            />
                            {r.label}
                          </span>
                        )
                      },
                      { label: 'Count', right: true, render: (r) => r.count },
                      {
                        label: 'Billed',
                        right: true,
                        render: (r) => (r.key === 'labour' ? '—' : money(r.billed))
                      },
                      { label: 'Paid', right: true, render: (r) => money(r.paid) },
                      {
                        label: 'Pending',
                        right: true,
                        render: (r) => (r.key === 'labour' ? '—' : money(r.pending))
                      }
                    ]}
                  />
                </SectionCard>

                <SectionCard title="Material by block">
                  <MiniTable
                    rows={p.blocks}
                    empty="No material bought yet."
                    columns={[
                      { label: 'Block', render: (r) => r.name },
                      { label: 'PIs', right: true, render: (r) => r.total.count },
                      { label: 'Ordered', right: true, render: (r) => money(r.total.billed) },
                      { label: 'Paid', right: true, render: (r) => money(r.total.paid) },
                      { label: 'Pending', right: true, render: (r) => money(r.total.pending) }
                    ]}
                  />
                </SectionCard>

                <SectionCard title="Top vendors">
                  <MiniTable
                    rows={d.top_vendors}
                    empty="No vendor PIs yet."
                    columns={[
                      { label: 'Vendor', render: (r) => r.name },
                      { label: 'PIs', right: true, render: (r) => r.count },
                      { label: 'Ordered', right: true, render: (r) => money(r.billed) },
                      { label: 'Paid', right: true, render: (r) => money(r.paid) }
                    ]}
                  />
                </SectionCard>

                <SectionCard title="Top material categories">
                  <MiniTable
                    rows={d.top_categories}
                    empty="No accepted quotation items yet."
                    columns={[
                      { label: 'Category', render: (r) => r.name },
                      { label: 'Ordered', right: true, render: (r) => money(r.amount) }
                    ]}
                  />
                </SectionCard>
              </div>

              <SectionCard title="Behind the totals">
                <div className="mb-3">
                  <SegmentedControl
                    ariaLabel="Documents"
                    size="sm"
                    value={tab}
                    onChange={setTab}
                    options={ITEM_TABS.map((t) => ({
                      ...t,
                      label: `${t.label} (${d.items[t.value].length})`
                    }))}
                  />
                </div>
                <TableCard>
                  <Table>
                    <THead>
                      <tr>
                        {itemColumns.map((c) => (
                          <Th key={c.label}>{c.label}</Th>
                        ))}
                        {withAmounts && <Th align="right">Billed</Th>}
                        <Th align="right">Paid</Th>
                        {withAmounts && <Th align="right">Pending</Th>}
                        {withAmounts && <Th>Status</Th>}
                      </tr>
                    </THead>
                    <TBody>
                      {!items.length && (
                        <EmptyRow colSpan={itemColumns.length + (withAmounts ? 4 : 1)}>
                          Nothing here.
                        </EmptyRow>
                      )}
                      {items.map((r) => (
                        <tr key={r.id}>
                          {itemColumns.map((c) => (
                            <Td key={c.label} className="text-gray-700">
                              {c.render(r)}
                            </Td>
                          ))}
                          {withAmounts && (
                            <Td align="right" numeric>
                              {money(r.billed)}
                            </Td>
                          )}
                          <Td align="right" numeric className="font-medium">
                            {money(r.paid)}
                          </Td>
                          {withAmounts && (
                            <Td
                              align="right"
                              numeric
                              className={r.pending > 0 ? 'text-warning-fg' : ''}
                            >
                              {money(r.pending)}
                            </Td>
                          )}
                          {withAmounts && (
                            <Td>
                              <StatusPill tone={STATUS_TONE[r.status] ?? 'neutral'} dense>
                                {STATUS_LABEL[r.status] ?? r.status}
                              </StatusPill>
                            </Td>
                          )}
                        </tr>
                      ))}
                    </TBody>
                  </Table>
                </TableCard>
                {items.length >= d.item_limit && (
                  <p className="mt-2 text-sm text-gray-500">Showing the latest {d.item_limit}.</p>
                )}
              </SectionCard>
            </>
          )
        )}
      </div>
    </Layout>
  );
};

export default ProjectExpenditureDetail;
